import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getPendingIdeas, approveOnboardingIdea, rejectOnboardingIdea, getAllUsers, getUnverifiedResolutions, verifyResolution, disputeResolution, getSettledIdeas, resolveSettledMarket, getUserOnboardingApplication, approveUser } from '../services/admin';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';

const AdminDashboardPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pending-ideas');
  const [pendingIdeas, setPendingIdeas] = useState([]);
  const [users, setUsers] = useState([]);
  const [unverifiedResolutions, setUnverifiedResolutions] = useState([]);
  const [settledMarkets, setSettledMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectionModal, setRejectionModal] = useState({ show: false, ideaId: null, reason: '' });
  const [disputeModal, setDisputeModal] = useState({ show: false, ideaId: null, reason: '', correctedPrice: '', correctedROI: '' });
  const [resolveModal, setResolveModal] = useState({ show: false, idea: null, outcome: 'win', actualPrice: '', roi: '' });
  const [applicationModal, setApplicationModal] = useState({ show: false, user: null, application: null });
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'pending-ideas') {
        const ideas = await getPendingIdeas();
        setPendingIdeas(ideas);
      } else if (activeTab === 'settled-markets') {
        const settled = await getSettledIdeas();
        setSettledMarkets(settled);
      } else if (activeTab === 'verify-resolutions') {
        const resolutions = await getUnverifiedResolutions();
        setUnverifiedResolutions(resolutions);
      } else if (activeTab === 'users') {
        const allUsers = await getAllUsers();
        setUsers(allUsers);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (ideaId, userId) => {
    try {
      setProcessingId(ideaId);
      await approveOnboardingIdea(ideaId, userId, currentUser.uid);
      await fetchData(); // Refresh the list
    } catch (error) {
      console.error('Error approving idea:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectClick = (ideaId) => {
    setRejectionModal({ show: true, ideaId, reason: '' });
  };

  const handleRejectConfirm = async () => {
    try {
      setProcessingId(rejectionModal.ideaId);
      await rejectOnboardingIdea(rejectionModal.ideaId, currentUser.uid, rejectionModal.reason);
      setRejectionModal({ show: false, ideaId: null, reason: '' });
      await fetchData(); // Refresh the list
    } catch (error) {
      console.error('Error rejecting idea:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleVerifyResolution = async (ideaId) => {
    try {
      setProcessingId(ideaId);
      await verifyResolution(ideaId, currentUser.uid, '');
      await fetchData(); // Refresh the list
    } catch (error) {
      console.error('Error verifying resolution:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDisputeClick = (ideaId) => {
    setDisputeModal({ show: true, ideaId, reason: '', correctedPrice: '', correctedROI: '' });
  };

  const handleDisputeConfirm = async () => {
    try {
      setProcessingId(disputeModal.ideaId);
      const correctedPrice = disputeModal.correctedPrice ? parseFloat(disputeModal.correctedPrice) : null;
      const correctedROI = disputeModal.correctedROI ? parseFloat(disputeModal.correctedROI) : null;
      
      await disputeResolution(disputeModal.ideaId, currentUser.uid, disputeModal.reason, correctedPrice, correctedROI);
      setDisputeModal({ show: false, ideaId: null, reason: '', correctedPrice: '', correctedROI: '' });
      await fetchData(); // Refresh the list
    } catch (error) {
      console.error('Error disputing resolution:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleResolveClick = (idea) => {
    setResolveModal({ 
      show: true, 
      idea, 
      outcome: 'win', 
      actualPrice: '', 
      roi: '' 
    });
  };

  const calculateROI = (entryPrice, actualPrice, position, stopLoss = null) => {
    if (!entryPrice || !actualPrice) return 0;
    
    const entry = parseFloat(entryPrice);
    let actual = parseFloat(actualPrice);
    const stop = stopLoss ? parseFloat(stopLoss) : null;
    
    // Apply stop loss if it exists and would limit the loss
    if (stop !== null) {
      if (position === 'Yes' || position === 'Long') {
        // For Yes/Long: stop loss limits downside, use stop loss if actual is below it
        if (actual < stop) {
          actual = stop;
        }
      } else if (position === 'No' || position === 'Short') {
        // For No/Short: stop loss limits upside loss, use stop loss if actual is above it
        if (actual > stop) {
          actual = stop;
        }
      }
    }
    
    // For Yes/Long positions: profit when price rises
    // Returns negative ROI if actual < entry (losing position)
    if (position === 'Yes' || position === 'Long') {
      return ((actual - entry) / entry) * 100;
    }
    
    // For No/Short positions: profit when price falls
    // Returns negative ROI if actual > entry (losing position)
    if (position === 'No' || position === 'Short') {
      return ((entry - actual) / entry) * 100;
    }
    
    return 0;
  };

  const handleResolveConfirm = async () => {
    try {
      if (!resolveModal.actualPrice || isNaN(resolveModal.actualPrice)) {
        toast.error('Please enter a valid actual price');
        return;
      }

      setProcessingId(resolveModal.idea.id);
      const actualPrice = parseFloat(resolveModal.actualPrice);
      const roi = calculateROI(resolveModal.idea.entryPrice, actualPrice, resolveModal.idea.position, resolveModal.idea.stopLoss);
      
      await resolveSettledMarket(resolveModal.idea.id, currentUser.uid, {
        outcome: resolveModal.outcome,
        actualPrice,
        roi,
      });
      
      setResolveModal({ show: false, idea: null, outcome: 'win', actualPrice: '', roi: '' });
      await fetchData(); // Refresh the list
    } catch (error) {
      console.error('Error resolving settled market:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleViewApplication = async (user) => {
    try {
      setLoading(true);
      const application = await getUserOnboardingApplication(user.id);
      setApplicationModal({ show: true, user, application });
    } catch (error) {
      console.error('Error loading application:', error);
      toast.error('Failed to load application');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      setProcessingId(userId);
      await approveUser(userId, currentUser.uid);
      await fetchData(); // Refresh the list
      setApplicationModal({ show: false, user: null, application: null });
    } catch (error) {
      console.error('Error approving user:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOnboardingStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white">
      <Navbar />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">Admin Dashboard</h1>
          <p className="text-xl text-primary-100">
            Manage users and moderate onboarding ideas
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('pending-ideas')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'pending-ideas'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pending Ideas
                {pendingIdeas.length > 0 && (
                  <span className="ml-2 bg-primary-100 text-primary-600 py-0.5 px-2 rounded-full text-xs">
                    {pendingIdeas.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('settled-markets')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'settled-markets'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Settled Markets
                {settledMarkets.length > 0 && (
                  <span className="ml-2 bg-orange-100 text-orange-600 py-0.5 px-2 rounded-full text-xs">
                    {settledMarkets.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('verify-resolutions')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'verify-resolutions'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Verify Resolutions
                {unverifiedResolutions.length > 0 && (
                  <span className="ml-2 bg-yellow-100 text-yellow-600 py-0.5 px-2 rounded-full text-xs">
                    {unverifiedResolutions.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'users'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Users
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <>
                {/* Pending Ideas Tab */}
                {activeTab === 'pending-ideas' && (
                  <div>
                    {pendingIdeas.length === 0 ? (
                      <div className="text-center py-12">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No pending ideas</h3>
                        <p className="mt-1 text-sm text-gray-500">All onboarding ideas have been reviewed.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {pendingIdeas.map((idea) => (
                          <div key={idea.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h3 className="text-lg font-semibold text-gray-900">{idea.title}</h3>
                                  {idea.isOnboardingIdea && (
                                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                                      Onboarding
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 mb-2">{idea.description}</p>
                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                  <span>By: <strong>{idea.authorName}</strong></span>
                                  <span>•</span>
                                  <span>{formatDate(idea.createdAt)}</span>
                                  <span>•</span>
                                  <span className="px-2 py-1 bg-gray-100 rounded">{idea.platform}</span>
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg text-sm">
                              <div>
                                <p className="text-gray-500">Entry</p>
                                <p className="font-semibold">{idea.entryPrice}¢</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Target</p>
                                <p className="font-semibold text-green-600">{idea.targetPrice}¢</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Risk Level</p>
                                <p className="font-semibold">{idea.riskLevel}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Potential Return</p>
                                <p className="font-semibold text-primary-600">
                                  {(((idea.targetPrice - idea.entryPrice) / idea.entryPrice) * 100).toFixed(1)}%
                                </p>
                              </div>
                            </div>

                            <div className="mb-4">
                              <p className="text-sm font-medium text-gray-700 mb-2">Reasoning:</p>
                              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{idea.reasoning}</p>
                            </div>

                            <div className="flex gap-3">
                              <button
                                onClick={() => handleApprove(idea.id, idea.authorId)}
                                disabled={processingId === idea.id}
                                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                              >
                                {processingId === idea.id ? 'Processing...' : '✓ Approve & Grant Access'}
                              </button>
                              <button
                                onClick={() => handleRejectClick(idea.id)}
                                disabled={processingId === idea.id}
                                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                              >
                                ✕ Reject
                              </button>
                              <button
                                onClick={() => navigate(`/ideas/${idea.id}`)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                              >
                                View Details
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Settled Markets Tab */}
                {activeTab === 'settled-markets' && (
                  <div>
                    {settledMarkets.length === 0 ? (
                      <div className="text-center py-12">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No settled markets</h3>
                        <p className="mt-1 text-sm text-gray-500">All expired markets have been resolved.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {settledMarkets.map((idea) => (
                          <div key={idea.id} className="border border-orange-200 rounded-lg p-6 hover:shadow-md transition-shadow bg-orange-50/30">
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h3 className="text-lg font-semibold text-gray-900">{idea.title}</h3>
                                  <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded">
                                    Settled {idea.daysSettled} day{idea.daysSettled !== 1 ? 's' : ''} ago
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 mb-2">{idea.description}</p>
                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                  <span>By: <strong>{idea.authorName}</strong></span>
                                  <span>•</span>
                                  <span>Position: <strong>{idea.position}</strong></span>
                                  <span>•</span>
                                  <span className="px-2 py-1 bg-gray-100 rounded">{idea.platform}</span>
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 p-4 bg-white rounded-lg text-sm border border-orange-100">
                              <div>
                                <p className="text-gray-500">Entry Price</p>
                                <p className="font-semibold">{idea.entryPrice}¢</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Target Price</p>
                                <p className="font-semibold text-green-600">{idea.targetPrice}¢</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Expected ROI</p>
                                <p className="font-semibold text-primary-600">
                                  {(((idea.targetPrice - idea.entryPrice) / idea.entryPrice) * 100).toFixed(1)}%
                                </p>
                              </div>
                            </div>

                            <div className="mb-4">
                              <a 
                                href={idea.marketUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                Check Final Price on {idea.platform}
                              </a>
                            </div>

                            <div className="flex gap-3">
                              <button
                                onClick={() => handleResolveClick(idea)}
                                disabled={processingId === idea.id}
                                className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                              >
                                {processingId === idea.id ? 'Processing...' : '📊 Resolve Market'}
                              </button>
                              <button
                                onClick={() => navigate(`/ideas/${idea.id}`)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                              >
                                View Details
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Verify Resolutions Tab */}
                {activeTab === 'verify-resolutions' && (
                  <div>
                    {unverifiedResolutions.length === 0 ? (
                      <div className="text-center py-12">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No resolutions to verify</h3>
                        <p className="mt-1 text-sm text-gray-500">All resolved ideas have been verified.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {unverifiedResolutions.map((idea) => (
                          <div key={idea.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h3 className="text-lg font-semibold text-gray-900">{idea.title}</h3>
                                  <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded">
                                    Pending Verification
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 mb-2">{idea.description}</p>
                                <div className="flex items-center gap-4 text-sm text-gray-500">
                                  <span>By: <strong>{idea.authorName}</strong></span>
                                  <span>•</span>
                                  <span>Resolved: {formatDate(idea.resolvedAt)}</span>
                                  <span>•</span>
                                  <span className="px-2 py-1 bg-gray-100 rounded">{idea.platform}</span>
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg text-sm">
                              <div>
                                <p className="text-gray-500">Entry Price</p>
                                <p className="font-semibold">{idea.entryPrice}¢</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Reported Exit</p>
                                <p className="font-semibold text-blue-600">{idea.actualPrice}¢</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Claimed ROI</p>
                                <p className={`font-semibold ${idea.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {idea.roi > 0 ? '+' : ''}{idea.roi?.toFixed(2)}%
                                </p>
                              </div>
                              <div>
                                <p className="text-gray-500">Outcome</p>
                                <p className={`font-semibold ${idea.outcome === 'win' ? 'text-green-600' : 'text-red-600'}`}>
                                  {idea.outcome?.toUpperCase()}
                                </p>
                              </div>
                            </div>

                            <div className="mb-4">
                              <a 
                                href={idea.marketUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                Verify on {idea.platform}
                              </a>
                            </div>

                            <div className="flex gap-3">
                              <button
                                onClick={() => handleVerifyResolution(idea.id)}
                                disabled={processingId === idea.id}
                                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                              >
                                {processingId === idea.id ? 'Processing...' : '✓ Verify Correct'}
                              </button>
                              <button
                                onClick={() => handleDisputeClick(idea.id)}
                                disabled={processingId === idea.id}
                                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                              >
                                ✗ Dispute
                              </button>
                              <button
                                onClick={() => navigate(`/ideas/${idea.id}`)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                              >
                                View Details
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                  <div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Email
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Role
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Onboarding Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Joined
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {users.map((user) => (
                            <tr key={user.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{user.displayName}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-500">{user.email}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {user.role}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getOnboardingStatusBadge(user.onboardingStatus)}`}>
                                  {user.onboardingStatus || 'N/A'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(user.createdAt)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                {user.onboardingStatus === 'pending' && (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleViewApplication(user)}
                                      className="text-blue-600 hover:text-blue-900 transition-colors"
                                    >
                                      View Application
                                    </button>
                                    <button
                                      onClick={() => handleApproveUser(user.id)}
                                      disabled={processingId === user.id}
                                      className="text-green-600 hover:text-green-900 disabled:text-gray-400 transition-colors"
                                    >
                                      {processingId === user.id ? 'Processing...' : 'Approve'}
                                    </button>
                                  </div>
                                )}
                                {user.onboardingStatus === 'approved' && (
                                  <span className="text-gray-400 text-xs">Approved</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Rejection Modal */}
      {rejectionModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Idea</h3>
            <p className="text-sm text-gray-600 mb-4">
              Provide a reason for rejection (optional). The user will be able to submit a new idea.
            </p>
            <textarea
              value={rejectionModal.reason}
              onChange={(e) => setRejectionModal({ ...rejectionModal, reason: e.target.value })}
              placeholder="e.g., Needs more detailed reasoning, market analysis missing, etc."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={handleRejectConfirm}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Confirm Rejection
              </button>
              <button
                onClick={() => setRejectionModal({ show: false, ideaId: null, reason: '' })}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolve Settled Market Modal */}
      {resolveModal.show && resolveModal.idea && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Resolve Settled Market</h3>
            <p className="text-sm text-gray-600 mb-4">
              Check the market on {resolveModal.idea.platform} and enter the final settlement price.
            </p>
            
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">{resolveModal.idea.title}</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Entry:</span> <strong>{resolveModal.idea.entryPrice}¢</strong>
                </div>
                <div>
                  <span className="text-gray-500">Target:</span> <strong>{resolveModal.idea.targetPrice}¢</strong>
                </div>
                <div>
                  <span className="text-gray-500">Position:</span> <strong>{resolveModal.idea.position}</strong>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Outcome
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setResolveModal({ ...resolveModal, outcome: 'win' })}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    resolveModal.outcome === 'win'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Win
                </button>
                <button
                  onClick={() => setResolveModal({ ...resolveModal, outcome: 'loss' })}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    resolveModal.outcome === 'loss'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Loss
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Final Settlement Price (¢) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={resolveModal.actualPrice}
                onChange={(e) => {
                  const actualPrice = e.target.value;
                  const roi = actualPrice ? calculateROI(resolveModal.idea.entryPrice, actualPrice, resolveModal.idea.position, resolveModal.idea.stopLoss).toFixed(2) : '';
                  setResolveModal({ ...resolveModal, actualPrice, roi });
                }}
                placeholder="Enter final price (0-100)"
                min="0"
                max="100"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {resolveModal.actualPrice && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Calculated ROI:</strong>{' '}
                  <span className={`font-semibold ${parseFloat(resolveModal.roi) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {parseFloat(resolveModal.roi) > 0 ? '+' : ''}{resolveModal.roi}%
                  </span>
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleResolveConfirm}
                disabled={!resolveModal.actualPrice}
                className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                Resolve & Verify
              </button>
              <button
                onClick={() => setResolveModal({ show: false, idea: null, outcome: 'win', actualPrice: '', roi: '' })}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dispute Modal */}
      {disputeModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dispute Resolution</h3>
            <p className="text-sm text-gray-600 mb-4">
              Provide a reason for the dispute. Optionally provide corrected values.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Dispute <span className="text-red-500">*</span>
              </label>
              <textarea
                value={disputeModal.reason}
                onChange={(e) => setDisputeModal({ ...disputeModal, reason: e.target.value })}
                placeholder="e.g., Exit price doesn't match market data, incorrect calculation"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Corrected Exit Price (Optional)
              </label>
              <input
                type="number"
                value={disputeModal.correctedPrice}
                onChange={(e) => setDisputeModal({ ...disputeModal, correctedPrice: e.target.value })}
                placeholder="Enter correct exit price in cents"
                min="0"
                max="100"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Corrected ROI % (Optional)
              </label>
              <input
                type="number"
                value={disputeModal.correctedROI}
                onChange={(e) => setDisputeModal({ ...disputeModal, correctedROI: e.target.value })}
                placeholder="Enter correct ROI percentage"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <p className="text-xs text-gray-500 mb-4">
              If you provide corrected values, they will replace the user's reported values.
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleDisputeConfirm}
                disabled={!disputeModal.reason}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                Confirm Dispute
              </button>
              <button
                onClick={() => setDisputeModal({ show: false, ideaId: null, reason: '', correctedPrice: '', correctedROI: '' })}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Application Modal */}
      {applicationModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Application Review: {applicationModal.user?.displayName}
                </h3>
                <button
                  onClick={() => setApplicationModal({ show: false, user: null, application: null })}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {applicationModal.application ? (
                <div className="space-y-6">
                  {/* User Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Applicant Information</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Name:</span> <strong>{applicationModal.user?.displayName}</strong>
                      </div>
                      <div>
                        <span className="text-gray-500">Email:</span> <strong>{applicationModal.user?.email}</strong>
                      </div>
                      <div>
                        <span className="text-gray-500">Joined:</span> <strong>{formatDate(applicationModal.user?.createdAt)}</strong>
                      </div>
                      <div>
                        <span className="text-gray-500">Status:</span> 
                        <span className={`ml-1 px-2 py-1 text-xs font-medium rounded-full ${getOnboardingStatusBadge(applicationModal.user?.onboardingStatus)}`}>
                          {applicationModal.user?.onboardingStatus}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Application Content */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h4 className="font-medium text-gray-900 mb-4">Application Writeup</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <p className="text-gray-900">{applicationModal.application.title}</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <p className="text-gray-900">{applicationModal.application.description}</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                          {applicationModal.application.platform}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-gray-500 text-sm">Entry Price</p>
                          <p className="font-semibold">{applicationModal.application.entryPrice}¢</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-sm">Target Price</p>
                          <p className="font-semibold text-green-600">{applicationModal.application.targetPrice}¢</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-sm">Position</p>
                          <p className="font-semibold">{applicationModal.application.position}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-sm">Risk Level</p>
                          <p className="font-semibold">{applicationModal.application.riskLevel}</p>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reasoning</label>
                        <div className="bg-gray-50 p-3 rounded border">
                          <p className="text-gray-900 whitespace-pre-wrap">{applicationModal.application.reasoning}</p>
                        </div>
                      </div>

                      {applicationModal.application.marketUrl && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Market URL</label>
                          <a 
                            href={applicationModal.application.marketUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline text-sm"
                          >
                            {applicationModal.application.marketUrl}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => handleApproveUser(applicationModal.user.id)}
                      disabled={processingId === applicationModal.user.id}
                      className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                      {processingId === applicationModal.user.id ? 'Processing...' : '✓ Approve Application'}
                    </button>
                    <button
                      onClick={() => {
                        // Navigate to pending ideas tab to reject the idea
                        setApplicationModal({ show: false, user: null, application: null });
                        setActiveTab('pending-ideas');
                      }}
                      className="flex-1 bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 transition-colors font-medium"
                    >
                      ✕ Reject Application
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No Application Found</h3>
                  <p className="mt-1 text-sm text-gray-500">This user hasn't submitted an onboarding application yet.</p>
                  <div className="mt-4">
                    <button
                      onClick={() => handleApproveUser(applicationModal.user.id)}
                      disabled={processingId === applicationModal.user.id}
                      className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                      {processingId === applicationModal.user.id ? 'Processing...' : 'Approve Without Application'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminDashboardPage;

